import { FC, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { InputCheck, InputText, InputTextArea } from '@farmshare/ui-components';
import { useFormikContext } from 'formik';
import { ImageUploadInput } from './image-upload-input';
import { PricingFields } from './pricing-fields';
import type { Specification } from '../../types';

/**
 * Renders children only when the active tab index matches.
 * @param root0
 * @param root0.value
 * @param root0.index
 * @param root0.children
 */
const TabPanel: FC<{
  value: number;
  index: number;
  children: React.ReactNode;
}> = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/**
 * Tabbed form for adding/editing Extra options.
 * Tabs: "Basic Details" | "Pricing"
 * Used with the useModal save pattern.
 * @param props - Component props
 * @param props.capabilityId - Capability set ID for S3 image grouping
 * @param props.specifications - Extra-type specifications available for assignment
 * @returns Extra form component
 */
export const ExtraForm: FC<{
  capabilityId: string;
  specifications?: Specification[];
}> = ({ capabilityId, specifications = [] }) => {
  const [tab, setTab] = useState(0);
  const { values, setFieldValue } = useFormikContext<{
    specificationIds?: string[];
    [key: string]: unknown;
  }>();

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        <Tab label="Basic Details" />
        <Tab label="Pricing" />
        {specifications.length > 0 && <Tab label="Specifications" />}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Stack spacing={3}>
          <InputText label="Extra Option Name" nameOverride="title" required />

          <InputTextArea
            label="Description"
            nameOverride="description"
            rows={3}
          />

          <ImageUploadInput
            nameOverride="imageUrl"
            label="Image"
            capabilityId={capabilityId}
          />

          <InputText
            label="Printed Text"
            nameOverride="printedText"
            hint="Override how this item appears on the printed cutsheet"
          />

          <InputCheck label="Has additional charge" nameOverride="hasCharge" />
        </Stack>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <PricingFields />
      </TabPanel>

      {specifications.length > 0 && (
        <TabPanel value={tab} index={2}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Select which specifications apply to this extra option. Only
              &ldquo;Extra-Specific&rdquo; specifications are shown.
            </Typography>
            <Autocomplete
              multiple
              options={specifications}
              getOptionLabel={(opt) => opt.title}
              value={specifications.filter((s) =>
                (values.specificationIds || []).includes(s.id),
              )}
              onChange={(_, newValue) =>
                setFieldValue(
                  'specificationIds',
                  newValue.map((s) => s.id),
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select specifications..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      label={opt.title}
                      {...tagProps}
                      size="small"
                    />
                  );
                })
              }
            />
          </Stack>
        </TabPanel>
      )}
    </Box>
  );
};
